import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  CircleCheck,
  Gauge,
  Hammer,
  Palette,
  Trash2,
  Trees,
  TriangleAlert,
  X,
} from 'lucide-preact';
import type { TrackBehavior } from '../schema/index.ts';
import { ELEMENT_PRESETS } from '../editor/index.ts';
import type { CoasterRuntime, RuntimePreviewState, RuntimeSnapshot } from '../app/runtime-types.ts';
import { Disclosure, IconButton } from './primitives.tsx';
import { UI_COPY } from './ui-copy.ts';

const TOOL_LABELS = {
  build: 'Build',
  scenery: 'Scenery',
  look: 'Style',
  check: 'Ride Check',
} as const;

const TOOL_ICONS = {
  build: Hammer,
  scenery: Trees,
  look: Palette,
  check: Gauge,
} as const;

const OVERLAYS = [
  ['none', 'None'],
  ['speed', 'Speed'],
  ['vertical_g', 'Vertical G'],
  ['lateral_g', 'Lateral G'],
  ['curvature', 'Curvature'],
  ['clearance', 'Clearance'],
] as const;

const TRACK_THUMBNAIL_BASE = `${import.meta.env.BASE_URL}track-piece-thumbnails/`;

function behaviorNumber(
  behavior: RuntimeSnapshot['selectedSectionBehavior'],
  key: string,
  fallback: number,
): number {
  if (!behavior || !(key in behavior)) return fallback;
  const value = (behavior as unknown as Record<string, unknown>)[key];
  return typeof value === 'number' ? value : fallback;
}

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

interface EditorShellProps {
  readonly state: RuntimeSnapshot;
  readonly runtime: CoasterRuntime | null;
  readonly coordinateRecoveryEpoch: number;
  readonly confirmReset: boolean;
  readonly setConfirmReset: (value: boolean) => void;
}

function PreviewTray({
  preview,
  runtime,
  inline,
}: {
  readonly preview: RuntimePreviewState;
  readonly runtime: CoasterRuntime | null;
  readonly inline: boolean;
}) {
  const firstIssue = preview.issues[0];
  const content = (
    <>
      <span class="eyebrow">
        {preview.severity === 'blocker' ? 'Cannot build here' : 'Preview'}
      </span>
      <h2>{preview.label}</h2>
      <p>
        {firstIssue?.message ??
          (preview.minPlotMarginM !== null
            ? `${preview.minPlotMarginM.toFixed(0)} m of plot space remains.`
            : 'Check the highlighted change, then add it to your track.')}
      </p>
      {preview.finishImpact && (
        <dl class="preview-metrics" aria-label="Finish impact">
          <div>
            <dt>{preview.kind === 'finish' ? 'Straight-line gap' : 'Distance home'}</dt>
            <dd>
              {preview.finishImpact.distanceBeforeM.toFixed(0)} m →{' '}
              {preview.finishImpact.distanceAfterM.toFixed(0)} m
            </dd>
          </div>
          <div>
            <dt>Station bearing</dt>
            <dd>
              {preview.finishImpact.headingBeforeDegrees.toFixed(0)}° →{' '}
              {preview.finishImpact.headingAfterDegrees.toFixed(0)}°
            </dd>
          </div>
        </dl>
      )}
      {preview.autoBankComparison && (
        <dl class="preview-metrics" aria-label="Auto-bank force improvement">
          <div>
            <dt>Highlighted lateral G</dt>
            <dd>
              {preview.autoBankComparison.hotspotBeforeG.toFixed(2)} G →{' '}
              {preview.autoBankComparison.hotspotAfterG.toFixed(2)} G
            </dd>
          </div>
          <div>
            <dt>Overall lateral G</dt>
            <dd>
              {preview.autoBankComparison.globalBeforeG.toFixed(2)} G →{' '}
              {preview.autoBankComparison.globalAfterG.toFixed(2)} G
            </dd>
          </div>
          <div>
            <dt>Maximum bank</dt>
            <dd>
              {preview.autoBankComparison.maximumBankBeforeDegrees.toFixed(0)}° →{' '}
              {preview.autoBankComparison.maximumBankAfterDegrees.toFixed(0)}°
            </dd>
          </div>
        </dl>
      )}
      {(preview.generatedLengthM !== null || preview.addedNodeCount !== null) && (
        <dl class="preview-metrics" aria-label="Generated finish details">
          {preview.generatedLengthM !== null && (
            <div>
              <dt>{preview.kind === 'finish' ? 'Safe route track' : 'New track'}</dt>
              <dd>{preview.generatedLengthM.toFixed(0)} m</dd>
            </div>
          )}
          {preview.addedNodeCount !== null && (
            <div>
              <dt>Added points</dt>
              <dd>{preview.addedNodeCount}</dd>
            </div>
          )}
        </dl>
      )}
      <div>
        <button type="button" onClick={() => runtime?.cancelPreview()}>
          Cancel
        </button>
        <button
          class="primary-action"
          type="button"
          disabled={!preview.canAccept}
          onClick={() => runtime?.acceptPreview()}
        >
          {preview.acceptLabel}
        </button>
      </div>
    </>
  );
  return inline ? (
    <section
      class={`inline-build-preview preview-${preview.severity}`}
      role="region"
      aria-label="Track piece preview"
    >
      {content}
    </section>
  ) : (
    <section
      class={`preset-preview preview-${preview.severity}`}
      role="dialog"
      aria-label="Element preview"
    >
      {content}
    </section>
  );
}

function CoordinateInput({
  axis,
  value,
  min,
  max,
  step,
  recoveryResetKey,
  runtime,
}: {
  readonly axis: 'x' | 'y' | 'z';
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly recoveryResetKey: string;
  readonly runtime: CoasterRuntime | null;
}) {
  const [draft, setDraft] = useState(value.toFixed(1));
  const [recovery, setRecovery] = useState<string | null>(null);
  const skipNextBlur = useRef(false);
  const recoveryTimer = useRef<number | null>(null);
  const clearRecovery = () => {
    if (recoveryTimer.current !== null) window.clearTimeout(recoveryTimer.current);
    recoveryTimer.current = null;
    setRecovery(null);
  };
  const showRecovery = (message: string) => {
    clearRecovery();
    setRecovery(message);
    recoveryTimer.current = window.setTimeout(() => {
      recoveryTimer.current = null;
      setRecovery(null);
    }, 3500);
  };
  useEffect(
    () => () => {
      if (recoveryTimer.current !== null) window.clearTimeout(recoveryTimer.current);
    },
    [],
  );
  useLayoutEffect(() => {
    setDraft(value.toFixed(1));
    clearRecovery();
  }, [axis, value, recoveryResetKey]);
  const commit = () => {
    if (!draft.trim()) {
      setDraft(value.toFixed(1));
      showRecovery(`Blank value rejected; restored ${axis.toUpperCase()} to ${value.toFixed(1)}.`);
      return false;
    }
    const parsed = Number(draft);
    if (!Number.isFinite(parsed) || parsed < min || parsed > max) {
      const rejected = draft;
      setDraft(value.toFixed(1));
      showRecovery(
        `${rejected} rejected; restored ${axis.toUpperCase()} to ${value.toFixed(1)}. Use ${min.toFixed(1)} to ${max.toFixed(1)}.`,
      );
      return false;
    }
    if (Math.abs(parsed - value) < 0.0001) {
      setDraft(value.toFixed(1));
      clearRecovery();
      return false;
    }
    clearRecovery();
    runtime?.setNodeCoordinate(axis, parsed);
    return true;
  };
  const feedbackId = `coordinate-${axis}-recovery`;
  return (
    <label class={recovery ? 'has-input-recovery' : undefined}>
      <span>{axis.toUpperCase()}</span>
      <input
        data-testid={`coordinate-${axis}`}
        aria-invalid="false"
        aria-describedby={recovery ? feedbackId : undefined}
        type="number"
        min={min}
        max={max}
        step={step}
        value={draft}
        onFocus={clearRecovery}
        onInput={(event) => {
          clearRecovery();
          setDraft(event.currentTarget.value);
        }}
        onBlur={() => {
          if (skipNextBlur.current) {
            skipNextBlur.current = false;
            return;
          }
          commit();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            skipNextBlur.current = true;
            commit();
            event.currentTarget.blur();
          } else if (event.key === 'Escape') {
            skipNextBlur.current = true;
            setDraft(value.toFixed(1));
            clearRecovery();
            event.currentTarget.blur();
          }
        }}
      />
      {recovery && (
        <small
          id={feedbackId}
          data-testid={`coordinate-${axis}-recovery`}
          role="status"
          aria-live="polite"
        >
          {recovery}
        </small>
      )}
    </label>
  );
}

export function EditorShell({
  state,
  runtime,
  coordinateRecoveryEpoch,
  confirmReset,
  setConfirmReset,
}: EditorShellProps) {
  const [compactExpanded, setCompactExpanded] = useState(false);
  const [confirmResetScene, setConfirmResetScene] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);
  const [heightDisplay, setHeightDisplay] = useState(state.selectedHeightM ?? 1);
  const [bankDisplay, setBankDisplay] = useState(state.selectedBankDegrees ?? 0);
  useLayoutEffect(
    () => setHeightDisplay(state.selectedHeightM ?? 1),
    [state.selectedNodeId, state.selectedHeightM],
  );
  useLayoutEffect(
    () => setBankDisplay(state.selectedBankDegrees ?? 0),
    [state.selectedNodeId, state.selectedBankDegrees],
  );
  const sectionCount = Math.max(
    0,
    state.topologyStatus === 'open' ? state.nodeCount - 1 : state.nodeCount,
  );
  const selectedBehavior = state.selectedSectionBehavior;
  const readinessSummary = state.validationPending
    ? 'Checking your coaster…'
    : state.fullValidation.pending
      ? 'Checking the full ride…'
      : state.fullValidation.status === 'blocked'
        ? `${state.fullValidation.issues.length || 1} ${
            state.fullValidation.issues.length === 1 ? 'Problem' : 'Problems'
          } to Fix`
        : state.fullValidation.technicalEligible && state.fullValidation.intensity
          ? UI_COPY.editor.ready
          : state.validation.blockerCount
            ? `${state.validation.blockerCount} ${
                state.validation.blockerCount === 1 ? 'Problem' : 'Problems'
              } to Fix`
            : UI_COPY.editor.ready;
  const readinessDetail = state.validationPending
    ? 'Checking track and train movement'
    : state.fullValidation.pending
      ? 'Checking speed, forces, and stalls'
      : state.fullValidation.technicalEligible
        ? `${state.fullValidation.intensity?.label ?? 'Unrated'} intensity`
        : state.testAllowed
          ? 'A test ride is available while you make fixes'
          : 'Fix the highlighted track problem before riding';

  const importFile = (file: File | undefined) => {
    if (!file) return;
    void file.text().then((text) => runtime?.importProject(text));
  };

  return (
    <>
      <nav class="tool-rail" aria-label="Editor tools">
        {(Object.keys(TOOL_LABELS) as (keyof typeof TOOL_LABELS)[]).map((tool) =>
          (() => {
            const ToolIcon = TOOL_ICONS[tool];
            return (
              <button
                type="button"
                class={state.activeTool === tool ? 'is-active' : undefined}
                aria-pressed={state.activeTool === tool}
                onClick={() => {
                  runtime?.setTool(tool);
                  setCompactExpanded(true);
                }}
              >
                <ToolIcon aria-hidden="true" size={19} strokeWidth={2.1} />
                <small>{TOOL_LABELS[tool]}</small>
              </button>
            );
          })(),
        )}
      </nav>

      <button
        class={`compact-editor-toggle ${compactExpanded ? 'is-expanded' : ''}`}
        type="button"
        aria-expanded={compactExpanded}
        onClick={() => setCompactExpanded(!compactExpanded)}
      >
        {compactExpanded ? 'Hide controls' : `${TOOL_LABELS[state.activeTool]} controls`}
      </button>

      <aside
        class={`context-inspector ${compactExpanded ? 'is-expanded' : ''}`}
        aria-label={`${TOOL_LABELS[state.activeTool]} controls`}
      >
        <div class="inspector-heading">
          <div>
            <span class="eyebrow">{TOOL_LABELS[state.activeTool]}</span>
            <h2>
              {state.activeTool === 'build'
                ? (state.selectedSectionLabel ??
                  state.selectedNodeLabel ??
                  UI_COPY.editor.buildTrack)
                : state.activeTool === 'scenery'
                  ? 'Park pieces'
                  : state.activeTool === 'look'
                    ? 'Ride style'
                    : UI_COPY.editor.rideCheck}
            </h2>
          </div>
          <div class="inspector-heading-actions">
            {state.activeTool === 'build' && state.canDeleteLastPiece && (
              <button
                class="delete-last-piece"
                type="button"
                onClick={() => runtime?.deleteLastBuiltPiece()}
              >
                <Trash2 aria-hidden="true" size={15} />
                Delete Last Piece
              </button>
            )}
            <button
              class="mobile-close"
              type="button"
              onClick={() => setCompactExpanded(false)}
              aria-label="Close controls"
            >
              <X aria-hidden="true" size={19} />
            </button>
          </div>
        </div>

        {state.activeTool === 'build' && (
          <div class="inspector-scroll" data-testid="build-panel">
            <div class="segmented-control" role="group" aria-label="Editing mode">
              <button
                type="button"
                aria-pressed={state.editorMode === 'basic'}
                onClick={() => runtime?.setEditorMode('basic')}
              >
                Basic
              </button>
              <button
                type="button"
                aria-pressed={state.editorMode === 'advanced'}
                onClick={() => runtime?.setEditorMode('advanced')}
              >
                Advanced
              </button>
            </div>

            {state.editorMode === 'basic' && (
              <div class="inline-callout basic-piece-callout">
                <b>{UI_COPY.editor.editPiece}</b>
                <small>Choose a track piece to change it.</small>
              </div>
            )}

            {state.editorMode === 'advanced' && (
              <div
                class="segmented-control selection-target-control"
                role="group"
                aria-label="Choose what to edit"
              >
                <button
                  type="button"
                  aria-pressed={state.selectionTarget === 'piece'}
                  onClick={() => runtime?.setSelectionTarget('piece')}
                >
                  {UI_COPY.editor.editPiece}
                </button>
                <button
                  type="button"
                  aria-pressed={state.selectionTarget === 'shape'}
                  onClick={() => runtime?.setSelectionTarget('shape')}
                >
                  Edit Shape
                </button>
              </div>
            )}

            {state.nodeCount > 0 ? (
              <section class="control-section">
                <div class="control-heading">
                  <strong>
                    {state.editorMode === 'basic' && state.selectionTarget === 'piece'
                      ? (state.selectedSectionLabel ?? 'Track Piece')
                      : state.selectedNodeId === state.buildEnd?.nodeId
                        ? 'Track Exit'
                        : state.editorMode === 'advanced'
                          ? 'Shape Handles'
                          : 'Shape Point'}
                  </strong>
                  <small data-testid="node-selection-count">
                    {state.editorMode === 'basic' && state.selectionTarget === 'piece'
                      ? state.selectedSectionId
                        ? 'Change speed or ride settings'
                        : 'Click the rails to choose a piece'
                      : state.selectedNodeId === state.buildEnd?.nodeId
                        ? 'New pieces attach here'
                        : state.editorMode === 'advanced'
                          ? state.selectedNodeIds.length > 1
                            ? `${state.selectedNodeIds.length} shape handles selected · changes apply to all`
                            : `${state.selectedNodeIds.length || 0} selected`
                          : 'Moves the track through one spot'}
                  </small>
                </div>
                {state.editorMode === 'advanced' && (
                  <div class="number-strip" role="group" aria-label="Select shape handle">
                    {Array.from({ length: state.nodeCount }, (_, index) => {
                      const selected = state.selectedNodeIds.includes(state.nodeIds[index] ?? '');
                      const primary = state.selectedNodeIndex === index;
                      return (
                        <button
                          type="button"
                          class={
                            `${selected ? 'is-selected' : ''} ${primary ? 'is-primary' : ''}`.trim() ||
                            undefined
                          }
                          aria-label={`Select shape handle ${index + 1}${primary ? ', primary' : ''}`}
                          aria-pressed={selected}
                          onClick={(event) => runtime?.selectNode(index, event.shiftKey)}
                        >
                          {index + 1}
                        </button>
                      );
                    })}
                  </div>
                )}
                {state.editorMode === 'advanced' && state.selectedNodeId && (
                  <>
                    <label class="range-control">
                      <span>
                        <b>Height</b>
                        <output>{heightDisplay.toFixed(1)} m</output>
                      </span>
                      <input
                        data-testid="elevation-slider"
                        type="range"
                        aria-label="Track point height"
                        min="1"
                        max="65"
                        step="0.5"
                        value={heightDisplay}
                        onPointerDown={() => runtime?.beginEdit(true)}
                        onPointerUp={() => runtime?.commitEdit()}
                        onInput={(event) => {
                          const value = Number(event.currentTarget.value);
                          setHeightDisplay(value);
                          runtime?.setElevation(value);
                        }}
                        onChange={() => runtime?.commitEdit()}
                        onBlur={() => runtime?.commitEdit()}
                      />
                    </label>
                    <label class="range-control">
                      <span>
                        <b>Bank</b>
                        <output>{bankDisplay.toFixed(0)}°</output>
                      </span>
                      <input
                        data-testid="bank-slider"
                        type="range"
                        aria-label="Track bank angle"
                        min="-90"
                        max="90"
                        step="5"
                        value={bankDisplay}
                        onPointerDown={() => runtime?.beginEdit(true)}
                        onPointerUp={() => runtime?.commitEdit()}
                        onInput={(event) => {
                          const value = Number(event.currentTarget.value);
                          setBankDisplay(value);
                          runtime?.setBank(value);
                        }}
                        onChange={() => runtime?.commitEdit()}
                        onBlur={() => runtime?.commitEdit()}
                      />
                    </label>
                    {(state.selectedRollWindingMixed ||
                      Math.abs(state.selectedRollWindingDegrees ?? 0) > 0.001) && (
                      <div class="inline-callout" data-testid="inversion-winding-control">
                        <span>
                          <b>Inversion roll</b>{' '}
                          {state.selectedRollWindingMixed
                            ? 'Mixed'
                            : `${state.selectedRollWindingDegrees!.toFixed(0)}°`}
                        </span>
                        <small>
                          Roll winding is separate from the safe −90° to 90° Bank control.
                        </small>
                        {state.editorMode === 'advanced' && (
                          <button type="button" onClick={() => runtime?.previewRemoveWinding()}>
                            Remove inversion winding
                          </button>
                        )}
                      </div>
                    )}
                    {state.editorMode === 'advanced' && state.selectedPosition && (
                      <div class="coordinate-editor" aria-label="Selected point coordinates">
                        {(['x', 'y', 'z'] as const).map((axis) => (
                          <CoordinateInput
                            axis={axis}
                            value={state.selectedPosition![axis]}
                            min={
                              axis === 'x'
                                ? state.plotBounds.minX
                                : axis === 'z'
                                  ? state.plotBounds.minZ
                                  : state.plotBounds.minY
                            }
                            max={
                              axis === 'x'
                                ? state.plotBounds.maxX
                                : axis === 'z'
                                  ? state.plotBounds.maxZ
                                  : state.plotBounds.maxY
                            }
                            step={axis === 'y' ? 0.5 : state.snappingEnabled ? 1 : 0.1}
                            recoveryResetKey={`${state.selectedNodeId}:${state.editorRevision}:${coordinateRecoveryEpoch}`}
                            runtime={runtime}
                          />
                        ))}
                        <label class="toggle-row snapping-toggle">
                          <input
                            type="checkbox"
                            checked={state.snappingEnabled}
                            onChange={(event) => runtime?.setSnapping(event.currentTarget.checked)}
                          />
                          <span>Snap to grid</span>
                        </label>
                      </div>
                    )}
                    {state.editorMode === 'advanced' && (
                      <div
                        class="nudge-pad axis-nudge-pad"
                        aria-label="Move selected Shape Handles"
                      >
                        <button
                          type="button"
                          aria-label="Move Z positive"
                          onClick={() => runtime?.nudgeSelected(0, 1)}
                        >
                          Z+
                        </button>
                        <button
                          data-testid="nudge-left"
                          type="button"
                          aria-label="Move X negative"
                          onClick={() => runtime?.nudgeSelected(-1, 0)}
                        >
                          X−
                        </button>
                        <button
                          type="button"
                          aria-label="Move Z negative"
                          onClick={() => runtime?.nudgeSelected(0, -1)}
                        >
                          Z−
                        </button>
                        <button
                          data-testid="nudge-right"
                          type="button"
                          aria-label="Move X positive"
                          onClick={() => runtime?.nudgeSelected(1, 0)}
                        >
                          X+
                        </button>
                      </div>
                    )}
                    {state.editorMode === 'advanced' && (
                      <div class="button-grid compact-grid">
                        <button type="button" onClick={() => runtime?.alignSelected('x')}>
                          Align X
                        </button>
                        <button type="button" onClick={() => runtime?.alignSelected('z')}>
                          Align Z
                        </button>
                        <button type="button" onClick={() => runtime?.alignSelected('y')}>
                          Align height
                        </button>
                        <button
                          type="button"
                          disabled={state.selectedNodeIds.length < 2}
                          onClick={() => runtime?.rotateSelected(-15)}
                        >
                          Rotate −15°
                        </button>
                        <button
                          type="button"
                          disabled={state.selectedNodeIds.length < 2}
                          onClick={() => runtime?.rotateSelected(15)}
                        >
                          Rotate +15°
                        </button>
                        <button type="button" onClick={() => runtime?.smoothSelected()}>
                          Smooth
                        </button>
                        <button
                          type="button"
                          disabled={state.autoBankPending}
                          onClick={() => runtime?.autoBankSelected()}
                        >
                          {state.autoBankPending ? 'Analyzing…' : 'Auto-bank'}
                        </button>
                        <button type="button" onClick={() => runtime?.focusSelection()}>
                          Focus point
                        </button>
                        <button
                          class="danger-action"
                          type="button"
                          onClick={() => runtime?.deleteSelected()}
                        >
                          Delete point
                        </button>
                      </div>
                    )}
                  </>
                )}
              </section>
            ) : (
              <div class="empty-callout">
                <strong>Start with one station</strong>
                <span>
                  Pick its spot, preview it, then click the station button again to place it.
                </span>
                <div
                  class="segmented-control station-position-control"
                  role="group"
                  aria-label="Station starting position"
                >
                  {(
                    [
                      ['near', 'Near edge'],
                      ['center', 'Center'],
                      ['far', 'Far edge'],
                    ] as const
                  ).map(([value, label]) => (
                    <button
                      type="button"
                      aria-pressed={state.stationPlacement === value}
                      onClick={() => runtime?.setStationPlacement(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  data-testid="place-station"
                  class={`primary-action ${state.preview?.presetId === 'station' ? 'is-previewing' : ''}`}
                  type="button"
                  onClick={() => runtime?.selectBuildPiece('station')}
                >
                  {state.preview?.presetId === 'station' ? 'Place Station' : 'Preview Station'}
                </button>
                {state.preview?.presetId === 'station' && (
                  <small class="placement-hint">Station previewed on the plot.</small>
                )}
              </div>
            )}

            {sectionCount > 0 && (state.editorMode === 'advanced' || state.selectedSectionId) && (
              <section class="control-section">
                <div class="control-heading">
                  <strong>
                    {state.editorMode === 'advanced' ? 'Track Pieces' : 'Track Piece'}
                  </strong>
                  <small>
                    {state.editorMode === 'advanced'
                      ? 'Controls the train between handles'
                      : 'Controls how the train behaves here'}
                  </small>
                </div>
                {state.editorMode === 'advanced' && (
                  <div
                    class="number-strip section-strip"
                    role="group"
                    aria-label="Select track piece"
                  >
                    {Array.from({ length: sectionCount }, (_, index) => (
                      <button
                        type="button"
                        class={state.selectedSectionIndex === index ? 'is-selected' : undefined}
                        aria-label={`Select track piece ${index + 1}`}
                        title={`Piece ${index + 1}: ${state.sectionKinds[index] ?? 'normal'}`}
                        aria-pressed={state.selectedSectionIndex === index}
                        onClick={() => runtime?.selectSection(index)}
                      >
                        <span>{index + 1}</span>
                        {state.sectionKinds[index] !== 'normal' && (
                          <small>{state.sectionKinds[index]}</small>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {state.selectedSectionId && (
                  <>
                    <label class="select-control">
                      <span>Section type</span>
                      <select
                        value={selectedBehavior?.kind ?? 'normal'}
                        onChange={(event) =>
                          runtime?.setSectionBehavior(
                            event.currentTarget.value as TrackBehavior['kind'],
                          )
                        }
                      >
                        <option value="normal">Normal track</option>
                        <option value="station">Station</option>
                        <option value="lift">Lift</option>
                        <option value="launch">Launch</option>
                        <option value="brake">Brakes</option>
                        <option value="photo">Photo marker</option>
                      </select>
                    </label>
                    <SectionSettings
                      behavior={selectedBehavior}
                      runtime={runtime}
                      advanced={state.editorMode === 'advanced'}
                    />
                    <div class="button-grid compact-grid">
                      {state.editorMode === 'advanced' && (
                        <button type="button" onClick={() => runtime?.insertSelectedSection()}>
                          Insert point
                        </button>
                      )}
                      <button type="button" onClick={() => runtime?.duplicateSelectedSection()}>
                        Duplicate
                      </button>
                    </div>
                  </>
                )}
              </section>
            )}

            {state.nodeCount > 0 && (
              <section
                class={`control-section ${state.topologyStatus === 'open' ? 'next-piece-builder' : ''}`}
              >
                <div class="control-heading">
                  <strong>
                    {state.topologyStatus === 'open' ? 'Choose Next Piece' : 'Add or Change'}
                  </strong>
                  <small>
                    {state.topologyStatus === 'open'
                      ? 'Click once to preview. Click again to build.'
                      : state.selectedSectionId
                        ? 'Applies to the selected piece'
                        : state.editorMode === 'basic' && state.selectionTarget === 'shape'
                          ? `Switch to ${UI_COPY.editor.editPiece} to change existing track`
                          : 'Click the rails to select a track piece'}
                  </small>
                </div>
                {(['shape', 'system', 'moment'] as const).map((category) => {
                  const presets = ELEMENT_PRESETS.filter(
                    (preset) =>
                      preset.category === category && preset.id !== 'station' && !preset.hidden,
                  );
                  return (
                    <div class="preset-group">
                      <strong>
                        {category === 'shape'
                          ? 'Track Shape'
                          : category === 'system'
                            ? 'Speed & Safety'
                            : 'Ride Moment'}
                      </strong>
                      <div class="preset-grid">
                        {presets.map((preset) => (
                          <button
                            data-testid={`preset-${preset.id}`}
                            type="button"
                            class={`bc-track-piece-button preset-${preset.category} ${state.preview?.presetId === preset.id ? 'is-previewing' : ''}`}
                            aria-pressed={state.preview?.presetId === preset.id}
                            aria-label={
                              state.preview?.presetId === preset.id
                                ? state.preview.canAccept
                                  ? `Place ${preset.label}`
                                  : `${preset.label} cannot be placed`
                                : `Preview ${preset.label}`
                            }
                            onClick={() => runtime?.selectBuildPiece(preset.id)}
                            title={preset.description}
                          >
                            <img
                              src={`${TRACK_THUMBNAIL_BASE}${preset.id}.webp`}
                              alt=""
                              width="144"
                              height="80"
                              loading="lazy"
                              aria-hidden="true"
                            />
                            <b>
                              {state.preview?.presetId === preset.id
                                ? state.preview.canAccept
                                  ? `Build ${preset.label}`
                                  : `Can't Place`
                                : preset.label}
                            </b>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
                <div class="piece-placement-actions" aria-live="polite">
                  <span>
                    {state.preview?.kind === 'piece' ? (
                      <>
                        <b>
                          {state.preview.canAccept
                            ? `${state.preview.label} previewed.`
                            : `${state.preview.label} cannot be placed here.`}
                        </b>{' '}
                        {state.preview.canAccept
                          ? state.canDeleteLastPiece
                            ? `Next ${state.preview.label} ready. Click the tile to repeat it.`
                            : 'Click it again to build.'
                          : state.preview.issues.find((issue) => issue.severity === 'blocker')
                              ?.message}
                      </>
                    ) : (
                      'Choose a piece to see where it will go.'
                    )}
                  </span>
                  <div>
                    {state.preview?.kind === 'piece' && (
                      <button type="button" onClick={() => runtime?.cancelPreview()}>
                        Clear Preview
                      </button>
                    )}
                  </div>
                </div>
              </section>
            )}

            <section class="control-section">
              <div class="control-heading">
                <strong>Finish to Station</strong>
                <small>
                  {state.topologyStatus === 'open'
                    ? `${state.buildEnd?.guidance.distanceM.toFixed(0) ?? '—'} m away`
                    : state.topologyStatus}
                </small>
              </div>
              {state.topologyStatus === 'open' && state.buildEnd ? (
                <>
                  <div class="finish-metrics" aria-label="Finish guidance">
                    <span>
                      <b>{state.buildEnd.guidance.distanceM.toFixed(0)} m</b>
                      to station
                    </span>
                    <span>
                      <b>{Math.abs(state.buildEnd.guidance.elevationDeltaM).toFixed(0)} m</b>
                      {state.buildEnd.guidance.elevationDeltaM > 0
                        ? 'climb needed'
                        : state.buildEnd.guidance.elevationDeltaM < 0
                          ? 'drop needed'
                          : 'level'}
                    </span>
                  </div>
                  <small class="finish-guidance">{state.buildEnd.guidance.summary}</small>
                  <button
                    data-testid="finish-to-station"
                    class="primary-action wide-button"
                    type="button"
                    disabled={
                      state.finishChecking ||
                      (!state.buildEnd.guidance.directJoinEligible &&
                        !state.buildEnd.guidance.generatedFinishEligible)
                    }
                    onClick={() => runtime?.finishToStation()}
                  >
                    {state.finishChecking ? 'Checking Finish…' : 'Finish to Station'}
                  </button>
                  <small class="finish-guidance">
                    The game tests the complete ride before showing a finish preview.
                  </small>
                  {state.editorMode === 'advanced' && (
                    <details class="advanced-finish-options">
                      <summary>More finish options</summary>
                      <button
                        type="button"
                        disabled={!state.buildEnd.guidance.directJoinEligible}
                        onClick={() => runtime?.connectEndpoints()}
                      >
                        Direct Join
                      </button>
                    </details>
                  )}
                </>
              ) : state.topologyStatus === 'closed' ? (
                <small>Track is closed. Open Ride Check to review it.</small>
              ) : state.topologyStatus === 'incomplete' ? (
                <small>Place a station and build more track first.</small>
              ) : (
                <small>Repair the track before finishing it.</small>
              )}
            </section>

            {state.editorMode === 'advanced' && (
              <section class="control-section developer-tools">
                <strong>Development JSON</strong>
                <div class="button-grid">
                  <button
                    type="button"
                    onClick={() =>
                      download(runtime?.exportProject() ?? new Blob(), 'buncha-coaster.json')
                    }
                  >
                    Export
                  </button>
                  <button type="button" onClick={() => importRef.current?.click()}>
                    Import
                  </button>
                </div>
                <input
                  ref={importRef}
                  class="sr-only"
                  type="file"
                  accept="application/json,.json"
                  onChange={(event) => importFile(event.currentTarget.files?.[0])}
                />
              </section>
            )}
          </div>
        )}

        {state.activeTool === 'scenery' && (
          <div class="inspector-scroll" data-testid="scenery-panel">
            <section class="control-section">
              <div class="control-heading">
                <strong>Add park pieces</strong>
                <small>{state.sceneryCount}/128</small>
              </div>
              <div class="scenery-palette">
                {(['tree', 'hedge', 'lamp', 'rock'] as const).map((asset) => (
                  <button type="button" onClick={() => runtime?.addScenery(asset)}>
                    <span aria-hidden="true">
                      {asset === 'tree'
                        ? '▲'
                        : asset === 'hedge'
                          ? '▰'
                          : asset === 'lamp'
                            ? '●'
                            : '◆'}
                    </span>
                    {asset[0]!.toUpperCase() + asset.slice(1)}
                  </button>
                ))}
              </div>
            </section>
            {state.sceneryCount > 0 && (
              <section class="control-section">
                <div class="control-heading">
                  <strong>Scene pieces</strong>
                  <small>Select in view or here</small>
                </div>
                <div class="number-strip">
                  {Array.from({ length: state.sceneryCount }, (_, index) => (
                    <button
                      type="button"
                      class={state.selectedSceneryIndex === index ? 'is-selected' : undefined}
                      onClick={() => runtime?.selectScenery(index)}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
                {state.selectedSceneryId && (
                  <>
                    <div class="nudge-pad">
                      <IconButton
                        label="Move scenery forward"
                        icon={ArrowUp}
                        type="button"
                        onClick={() => runtime?.nudgeSelectedScenery(0, -1)}
                      />
                      <IconButton
                        label="Move scenery left"
                        icon={ArrowLeft}
                        type="button"
                        onClick={() => runtime?.nudgeSelectedScenery(-1, 0)}
                      />
                      <IconButton
                        label="Move scenery backward"
                        icon={ArrowDown}
                        type="button"
                        onClick={() => runtime?.nudgeSelectedScenery(0, 1)}
                      />
                      <IconButton
                        label="Move scenery right"
                        icon={ArrowRight}
                        type="button"
                        onClick={() => runtime?.nudgeSelectedScenery(1, 0)}
                      />
                    </div>
                    <div class="button-grid compact-grid">
                      <button type="button" onClick={() => runtime?.rotateSelectedScenery(-15)}>
                        Rotate −15°
                      </button>
                      <button type="button" onClick={() => runtime?.rotateSelectedScenery(15)}>
                        Rotate +15°
                      </button>
                      <button type="button" onClick={() => runtime?.duplicateSelectedScenery()}>
                        Duplicate
                      </button>
                      <button
                        class="danger-action"
                        type="button"
                        onClick={() => runtime?.deleteSelectedScenery()}
                      >
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </section>
            )}
          </div>
        )}

        {state.activeTool === 'look' && (
          <div class="inspector-scroll" data-testid="look-panel">
            <section class="control-section">
              <div class="control-heading">
                <strong>Environment</strong>
                <small>Park Model is the default</small>
              </div>
              <div class="theme-grid">
                {(
                  [
                    ['park_model', 'Park Model', 'Clean outdoor diorama'],
                    ['toyworks', 'Toyworks', 'Playful tabletop workshop'],
                    ['afterglow', 'Afterglow', 'Neon night park'],
                  ] as const
                ).map(([id, label, detail]) => (
                  <button
                    type="button"
                    aria-pressed={state.appearance.themeId === id}
                    onClick={() => runtime?.setTheme(id)}
                  >
                    <strong>{label}</strong>
                    <small>{detail}</small>
                  </button>
                ))}
              </div>
            </section>
            <section class="control-section color-controls">
              <strong>Coaster colors</strong>
              {(['track', 'support', 'train'] as const).map((slot) => (
                <label>
                  <span>{slot[0]!.toUpperCase() + slot.slice(1)}</span>
                  <input
                    type="color"
                    value={state.appearance[`${slot}Color`]}
                    onInput={(event) =>
                      runtime?.setAppearanceColor(slot, event.currentTarget.value)
                    }
                  />
                </label>
              ))}
            </section>
            <section class="control-section">
              <strong>View</strong>
              <label class="toggle-row">
                <input
                  type="checkbox"
                  checked={state.supportsVisible}
                  onChange={(event) =>
                    runtime?.setLayerVisible('supports', event.currentTarget.checked)
                  }
                />
                <span>Show supports</span>
              </label>
              <label class="toggle-row">
                <input
                  type="checkbox"
                  checked={state.sceneryVisible}
                  onChange={(event) =>
                    runtime?.setLayerVisible('scenery', event.currentTarget.checked)
                  }
                />
                <span>Show scenery</span>
              </label>
              <button class="wide-button" type="button" onClick={() => runtime?.frameHeroPreview()}>
                Preview Game Card View
              </button>
            </section>
            <section class="control-section">
              <button
                class="danger-action wide-button"
                type="button"
                onClick={() => setConfirmResetScene(true)}
              >
                Reset Scene
              </button>
              {confirmResetScene && (
                <div class="inline-confirm" role="alertdialog" aria-label="Reset scene?">
                  <span>Remove scenery and color changes? Track work stays.</span>
                  <button type="button" onClick={() => setConfirmResetScene(false)}>
                    Cancel
                  </button>
                  <button
                    class="danger-action"
                    type="button"
                    onClick={() => {
                      runtime?.resetScene();
                      setConfirmResetScene(false);
                    }}
                  >
                    Reset
                  </button>
                </div>
              )}
            </section>
          </div>
        )}

        {state.activeTool === 'check' && (
          <div class="inspector-scroll" data-testid="checks-panel">
            <div
              class={`readiness-card ${state.validationPending || state.fullValidation.pending ? 'is-pending' : state.validation.blockerCount ? 'has-blockers' : state.validation.warningCount || state.fullValidation.status === 'warnings' ? 'has-warnings' : 'is-ready'}`}
            >
              <strong>{readinessSummary}</strong>
              <span>{readinessDetail}</span>
            </div>
            <section class="control-section full-run-summary" data-testid="full-run-summary">
              <div class="control-heading">
                <strong>{UI_COPY.editor.rideSummary}</strong>
                <small>
                  {state.fullValidation.pending
                    ? 'Checking'
                    : state.fullValidation.status === 'not_run'
                      ? 'Waiting'
                      : state.fullValidation.technicalEligible
                        ? 'Ride complete'
                        : 'Needs work'}
                </small>
              </div>
              {state.fullValidation.pending ? (
                <div class="empty-callout">
                  <strong>Checking the whole ride</strong>
                  <span>This should only take a moment.</span>
                </div>
              ) : state.fullValidation.status === 'not_run' ? (
                <div class="empty-callout">
                  <strong>Finish the circuit first</strong>
                  <span>The full simulation starts automatically on a rideable track.</span>
                </div>
              ) : (
                <>
                  <div class="validation-metrics">
                    <span>
                      <small>Intensity</small>
                      <strong>{state.fullValidation.intensity?.label ?? '--'}</strong>
                    </span>
                    <span>
                      <small>Top speed</small>
                      <strong>{state.fullValidation.topSpeedMph?.toFixed(1) ?? '--'} mph</strong>
                    </span>
                    <span>
                      <small>Warnings</small>
                      <strong>
                        {
                          state.fullValidation.issues.filter(
                            (issue) => issue.severity === 'warning',
                          ).length
                        }
                      </strong>
                    </span>
                    <span>
                      <small>Problems</small>
                      <strong>
                        {
                          state.fullValidation.issues.filter(
                            (issue) => issue.severity === 'blocker',
                          ).length
                        }
                      </strong>
                    </span>
                  </div>
                  <p class={`publish-readiness is-${state.fullValidation.status}`}>
                    {state.fullValidation.technicalEligible
                      ? `The ride completes with ${state.fullValidation.intensity?.label ?? 'unrated'} intensity.`
                      : state.fullValidation.testPolicy === 'diagnostic_only'
                        ? 'You can test the ride while you work through the remaining problem.'
                        : 'Fix the highlighted track problem before riding.'}
                  </p>
                  {state.fullValidation.issues.slice(0, 5).map((issue) => (
                    <button
                      type="button"
                      class={`issue-card issue-${issue.severity}`}
                      onClick={() => runtime?.editIssue(issue.id)}
                    >
                      {issue.severity === 'blocker' ? (
                        <TriangleAlert aria-hidden="true" size={18} />
                      ) : (
                        <CircleCheck aria-hidden="true" size={18} />
                      )}
                      <span>
                        <strong>
                          {issue.category === 'intensity'
                            ? `${issue.intensityTier ?? 'High'} forces`
                            : issue.severity === 'blocker'
                              ? 'Track problem'
                              : 'Rider warning'}
                        </strong>
                        <small>{issue.message}</small>
                        <small class="issue-action">{UI_COPY.editor.jumpToTrack}</small>
                      </span>
                    </button>
                  ))}
                  <Disclosure>
                    <dl class="validation-engineering-details">
                      <div>
                        <dt>Vertical G</dt>
                        <dd>
                          {state.fullValidation.minVerticalG?.toFixed(2) ?? '--'} to{' '}
                          {state.fullValidation.maxVerticalG?.toFixed(2) ?? '--'} G
                        </dd>
                      </div>
                      <div>
                        <dt>Lateral G</dt>
                        <dd>{state.fullValidation.maxAbsLateralG?.toFixed(2) ?? '--'} G</dd>
                      </div>
                    </dl>
                  </Disclosure>
                </>
              )}
            </section>
            <section class="control-section">
              <div class="control-heading">
                <strong>Track overlay</strong>
                <small>One at a time</small>
              </div>
              <div class="overlay-grid">
                {OVERLAYS.map(([id, label]) => (
                  <button
                    type="button"
                    aria-pressed={state.activeOverlay === id}
                    onClick={() => runtime?.setOverlay(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </section>
            <section class="control-section issue-list">
              <div class="control-heading">
                <strong>Track Problems</strong>
                <small>{state.validation.issues.length}</small>
              </div>
              {state.validationPending ? (
                <div class="empty-callout">
                  <strong>Checking track</strong>
                  <span>Results will appear automatically.</span>
                </div>
              ) : state.validation.issues.length ? (
                state.validation.issues.map((issue) => (
                  <button
                    type="button"
                    class={`issue-card issue-${issue.severity}`}
                    onClick={() => runtime?.focusIssue(issue.id)}
                  >
                    <TriangleAlert aria-hidden="true" size={18} />
                    <span>
                      <strong>{issue.severity === 'blocker' ? 'Fix required' : 'Warning'}</strong>
                      <small>{issue.message}</small>
                    </span>
                  </button>
                ))
              ) : (
                <div class="empty-callout">
                  <strong>No track problems</strong>
                  <span>
                    {state.fullValidation.status === 'warnings'
                      ? 'Your ride works. Check its intensity before sharing.'
                      : 'Your coaster is ready to ride.'}
                  </span>
                </div>
              )}
            </section>
          </div>
        )}
      </aside>

      <div class="editor-command-bar">
        <div class="build-summary" data-testid="build-summary">
          <strong>{readinessSummary}</strong>
          {state.validationPending || state.fullValidation.pending ? (
            <span>
              {state.fullValidation.pending ? 'Simulating the full ride…' : 'Checking the track…'}
            </span>
          ) : state.validation.warningCount > 0 ? (
            <button
              class="warning-summary-action"
              type="button"
              onClick={() => {
                const warning = state.validation.issues.find(
                  (issue) => issue.severity === 'warning',
                );
                runtime?.setTool('check');
                if (warning) runtime?.focusIssue(warning.id);
              }}
            >
              {state.validation.issues.find((issue) => issue.severity === 'warning')?.message ??
                'Review warning'}
            </button>
          ) : (
            <span>
              {state.diagnostics.trackLengthM.toFixed(0)} m · {state.nodeCount} points
            </span>
          )}
        </div>
      </div>

      {state.preview &&
        !state.preview.inline &&
        state.preview.kind !== 'station' &&
        state.preview.kind !== 'piece' && (
          <PreviewTray preview={state.preview} runtime={runtime} inline={false} />
        )}

      {state.tutorial.visible && (
        <section class="creator-challenge" aria-label="Coaster building help">
          <IconButton
            class="challenge-close"
            label="Dismiss challenge"
            icon={X}
            size="compact"
            type="button"
            onClick={() => runtime?.setTutorialVisible(false)}
          />
          <span class="eyebrow">
            {state.tutorial.kind === 'template' ? 'First Coaster' : 'New Coaster'} ·{' '}
            {state.tutorial.step}/3
          </span>
          {state.tutorial.kind === 'template' ? (
            <>
              <strong>
                {state.tutorial.step === 1
                  ? 'Edit a track piece'
                  : state.tutorial.step === 2
                    ? 'Edit a track piece and test'
                    : 'Pick a theme or new colors'}
              </strong>
              <small>
                {state.tutorial.step === 1
                  ? 'Click the rails, then try a Hill, Drop, or Turn.'
                  : state.tutorial.step === 2
                    ? 'Edit a lift, launch, or brake, then take a Test Ride.'
                    : 'Open Style and make it yours.'}
              </small>
            </>
          ) : (
            <>
              <strong>
                {state.tutorial.step === 1
                  ? 'Place the station'
                  : state.tutorial.step === 2
                    ? 'Build your route'
                    : 'Test your coaster'}
              </strong>
              <small>
                {state.tutorial.step === 1
                  ? 'Every new coaster starts with one station.'
                  : state.tutorial.step === 2
                    ? 'Add pieces from the mint exit, then use Finish to Station when you are ready.'
                    : 'Open Ride Check, fix any blockers, and take a Test Ride.'}
              </small>
            </>
          )}
          {state.tutorial.kind === 'template' && state.tutorial.step === 3 && (
            <button type="button" onClick={() => runtime?.advanceTutorial()}>
              Done
            </button>
          )}
        </section>
      )}

      {confirmReset && (
        <div class="reset-confirm" role="alertdialog" aria-label="Reset track?">
          <strong>Reset the whole track?</strong>
          <span>You can undo this change.</span>
          <div>
            <button type="button" onClick={() => setConfirmReset(false)}>
              Keep Editing
            </button>
            <button
              class="danger-action"
              type="button"
              onClick={() => {
                runtime?.resetFixture();
                setConfirmReset(false);
              }}
            >
              Reset Track
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function SectionSettings({
  behavior,
  runtime,
  advanced: _advanced,
}: {
  behavior: RuntimeSnapshot['selectedSectionBehavior'];
  runtime: CoasterRuntime | null;
  advanced: boolean;
}) {
  if (!behavior || behavior.kind === 'normal' || behavior.kind === 'photo') return null;
  const controls: {
    key: string;
    label: string;
    min: number;
    max: number;
    step: number;
    unit: string;
    fallback: number;
  }[] =
    behavior.kind === 'station'
      ? [
          {
            key: 'holdSeconds',
            label: 'Hold',
            min: 0,
            max: 20,
            step: 0.5,
            unit: 's',
            fallback: 1.5,
          },
          {
            key: 'dispatchSpeedMps',
            label: 'Dispatch speed',
            min: 0,
            max: 10,
            step: 0.5,
            unit: 'm/s',
            fallback: 2.5,
          },
        ]
      : behavior.kind === 'lift'
        ? [
            {
              key: 'targetSpeedMps',
              label: 'Lift speed',
              min: 1,
              max: 12,
              step: 0.5,
              unit: 'm/s',
              fallback: 5,
            },
          ]
        : behavior.kind === 'launch'
          ? [
              {
                key: 'targetSpeedMps',
                label: 'Launch speed',
                min: 5,
                max: 40,
                step: 0.5,
                unit: 'm/s',
                fallback: 20,
              },
              {
                key: 'accelerationMps2',
                label: 'Acceleration',
                min: 1,
                max: 15,
                step: 0.5,
                unit: 'm/s²',
                fallback: 7,
              },
            ]
          : [
              {
                key: 'targetSpeedMps',
                label: 'Brake speed',
                min: 0,
                max: 20,
                step: 0.5,
                unit: 'm/s',
                fallback: 6,
              },
              {
                key: 'decelerationMps2',
                label: 'Brake strength',
                min: 1,
                max: 15,
                step: 0.5,
                unit: 'm/s²',
                fallback: 5,
              },
            ];
  return (
    <div class="section-settings">
      {controls.map((control) => (
        <label class="range-control">
          <span>
            <b>{control.label}</b>
            <output>
              {behaviorNumber(behavior, control.key, control.fallback).toFixed(
                control.step < 1 ? 1 : 0,
              )}{' '}
              {control.unit}
            </output>
          </span>
          <input
            type="range"
            aria-label={control.label}
            min={control.min}
            max={control.max}
            step={control.step}
            value={behaviorNumber(behavior, control.key, control.fallback)}
            onInput={(event) =>
              runtime?.setSectionParameter(control.key, Number(event.currentTarget.value))
            }
          />
        </label>
      ))}
    </div>
  );
}
